import { NavItem, NavLink } from 'reactstrap';
import { AuthorizeContainer } from '../../shared/authorizeRoute';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavLinkTrackActive } from '../shared/NavLinkTrackActive';
import { IdentityRoles } from '../../configure/security/IdentityRoles';
import { useCurrentUserOrEmulatedSubscriptionId } from '../../globalState/subscriptions/useCurrentUserOrEmulatedSubscriptionId';
import { useCurrentUserRoles } from '../../api/account';
import { useMemo } from 'react';
import { useCurrentUserProfile } from '../../api/main/profiles/useCurrentUserProfile';
import { Guid } from 'guid-string';
import { useSubscription } from '../../api/main/subscriptions/useSubscription';
import { SubscriptionType } from '../../api/main/models/codeOnly/SubscriptionType';
import { ConditionalFragment } from 'react-conditionalfragment';

/**
 * Manage navigation menu for the app. 
 */
export const ManageNavigation = () => {
    const { t } = useTranslation();

    const { data: { model: currentProfile } } = useCurrentUserProfile();
    const { data: { roles } } = useCurrentUserRoles();
    const emulatedSubscriptionId = useCurrentUserOrEmulatedSubscriptionId();

    const subscriptionId = useMemo(() => {
        // Admins can use the emulated subscription.
        if (roles?.includes(IdentityRoles.Administration)) {
            return emulatedSubscriptionId;
        }

        // Non-admins can't use the emulated subscription.
        return currentProfile?.subscriptionId ?? Guid.empty;
    }, [currentProfile, emulatedSubscriptionId, roles]);

    // Load the subscription
    const { data: { model } } = useSubscription(subscriptionId ?? Guid.empty);
    if (model?.subscriptionType === SubscriptionType.Study && !roles?.includes(IdentityRoles.SubscriptionAdministration)) {
        return (<></>);
    }

    return (
        <>
            <AuthorizeContainer requireRole={IdentityRoles.DriverManagement}>
                <NavItem>
                    <NavLink tag={NavLinkTrackActive} to="/manage/drivers">
                        <FontAwesomeIcon icon="bus" className="nav-icon" />
                        <> {t('manageNavigtion.drivers', 'Drivers')}</>
                    </NavLink>
                </NavItem>
            </AuthorizeContainer>
            <AuthorizeContainer requireRole={IdentityRoles.SubscriptionAdministration}>
                <ConditionalFragment showIf={model?.subscriptionType !== SubscriptionType.Study}>
                    <NavItem>
                        <NavLink tag={NavLinkTrackActive} to="/manage/teams">
                            <FontAwesomeIcon icon="users" className="nav-icon" />
                            <> {t('manageNavigtion.teams', 'Teams')}</>
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink tag={NavLinkTrackActive} to="/manage/managers">
                            <FontAwesomeIcon icon="user" className="nav-icon" />
                            <> {t('manageNavigtion.managers', 'Managers')}</>
                        </NavLink>
                    </NavItem>
                </ConditionalFragment>
                <NavItem>
                    <NavLink tag={NavLinkTrackActive} to="/manage/sdk">
                        <FontAwesomeIcon icon="code" className="nav-icon" />
                        <> {t('manageNavigtion.api', 'API and SDK')}</>
                    </NavLink>
                </NavItem>
            </AuthorizeContainer>
        </>
    );
};
